import React, { useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import type { PathPolicyFormValues } from './PathPolicyForm'

export type CmfItem = PathPolicyFormValues['customMetadataFields'][number]

interface SelectCMFModalProps {
  isOpen: boolean
  onClose: () => void
  cmfList: CmfItem[]
  currentSelection: boolean[]
  onAdd: (newlySelectedIdxs: number[]) => void
}

const SelectCMFModal: React.FC<SelectCMFModalProps> = ({
  isOpen,
  onClose,
  cmfList,
  currentSelection,
  onAdd,
}) => {
  const { t } = useTranslation()

  const [localSel, setLocalSel] = useState<boolean[]>(currentSelection)

  useEffect(() => setLocalSel(currentSelection), [currentSelection])

  const toggleIdx = (idx: number, checked: boolean) => {
    setLocalSel(prev => {
      const next = [...prev]
      next[idx] = checked
      return next
    })
  }

  const computeNewlyChecked = (): number[] =>
    localSel
      .map((v, i) => (v && !currentSelection[i] ? i : -1))
      .filter(i => i !== -1)

  const handleAdd = () => {
    onAdd(computeNewlyChecked())
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('select_custom_metadata_fields', 'Select custom metadata fields')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Table>
            <Thead bg="appPaleGreyTwo">
              <Tr>
                <Th>{t('selection', 'Selection')}</Th>
                <Th>{t('name', 'Name')}</Th>
                <Th>{t('type', 'Type')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {cmfList.map((cm, idx) => (
                <Tr key={cm.name}>
                  <Td>
                    <Tooltip
                      label={t('selected', 'Selected')}
                      isDisabled={!currentSelection[idx]}
                      shouldWrapChildren
                    >
                      <span>
                        <Checkbox
                          isChecked={localSel[idx]}
                          isDisabled={currentSelection[idx]}
                          onChange={e => toggleIdx(idx, e.target.checked)}
                          cursor={currentSelection[idx] ? 'not-allowed' : 'pointer'}
                        />
                      </span>
                    </Tooltip>
                  </Td>
                  <Td>{cm.name}</Td>
                  <Td>{cm.schema.type}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </ModalBody>

        <ModalFooter gap="1em" borderTop="1px solid" borderColor="appGrey">
          <Button variant="outline" onClick={onClose}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleAdd}
            isDisabled={computeNewlyChecked().length === 0}
          >
            {t('add', 'Add')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default SelectCMFModal
